import React, { useEffect, useRef, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Collapse,
  DatePicker,
  Radio,
  Segmented,
  Spin,
  Table,
  Tag,
  Tooltip,
} from "antd";
import {
  DownOutlined,
} from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import Papa from "papaparse";
import toast from "react-hot-toast";
import { DspBean, PublisherBean } from "src/api/api";
import { useUser } from "../../../context/UserContext";
import { isAdmin, isReporting } from "../../../util/Util";
import { formDataLoader, FormDataType } from "../../../util/FormDataLoader";
import SelectionSection from "../ui/SelectionSection";
import ColumnFilterChecklist, { renderColumnFilterIcon } from "../ui/ColumnFilterChecklist";
import { sortReportRows } from "../reports/ReportUtil";
import rejectionReasonApi from "./RejectionReasonApi";
import {
  RejectionConfigType,
  RejectionConfigStatus,
  RejectionDurationMinutes,
  RejectionStatResponseBean,
} from "src/api/api";
import "./style.css";

const { RangePicker } = DatePicker;

type Scope = "publisher" | "dsp";
type Tab = "configure" | "generate";
type ReportMode = "publisher" | "dsp";
type Granularity = "daily" | "hourly";

type ReportRow = Record<string, string | number>;
type ReportData = { headers: string[]; rows: ReportRow[] };

const POLL_INTERVAL_MS = 30_000;

const formatCount = (n: number) => (n < 10 ? `0${n}` : `${n}`);
const toKey = (h: string) => h.toLowerCase().replace(/\s+/g, "_");

const REASON_TAG_COLORS = [
  "magenta", "red", "volcano", "orange", "gold",
  "lime", "green", "cyan", "blue", "geekblue", "purple",
];

const getReasonColor = (reason: string): string => {
  let hash = 0;
  for (let i = 0; i < reason.length; i++) {
    hash = (hash * 31 + reason.charCodeAt(i)) & 0xffff;
  }
  return REASON_TAG_COLORS[hash % REASON_TAG_COLORS.length];
};

const HEADER_DESCRIPTIONS: Record<string, string> = {
  "Generated Date": "Date the rejection was recorded",
  Hour: "Hour of the day (24h)",
  "Publisher Name": "Name of the publisher",
  "DSP Name": "Name of the DSP",
  "RR Reason": "Reason the request was rejected",
  "NBR Reason": "No-bid response reason",
  "Rejection Count": "Number of rejected requests",
};

/**
 * Maps API RejectionStatResponseBean[] into ReportData (headers + rows).
 * publisherId / dspId are resolved to display names via the lookup maps.
 */
const buildReportFromApi = (
  rows: RejectionStatResponseBean[],
  mode: ReportMode,
  granularity: Granularity,
  publisherMap: Map<number, string>,
  dspMap: Map<number, string>,
): ReportData => {
  const headers: string[] = ["Generated Date"];
  if (granularity === "hourly") headers.push("Hour");
  if (mode === "dsp") headers.push("DSP Name");
  headers.push("Publisher Name");
  headers.push(mode === "dsp" ? "NBR Reason" : "RR Reason");
  headers.push("Rejection Count");

  const reasonKey = mode === "dsp" ? "nbr_reason" : "rr_reason";

  const mappedRows: ReportRow[] = rows
    .filter((r) => r.rejectionReason !== "ACCEPTED")
    .map((r) => {
    const row: ReportRow = {
      generated_date: r.date,
      publisher_name: publisherMap.get(r.publisherId) ?? String(r.publisherId),
      [reasonKey]: r.rejectionReason,
      rejection_count: r.requestCount,
    };
    if (granularity === "hourly") row.hour = r.hour ?? "";
    if (mode === "dsp") row.dsp_name = dspMap.get(r.dspId) ?? String(r.dspId);
    return row;
  });

  return { headers, rows: mappedRows };
};

export const RejectionReason: React.FC = () => {
  const { user } = useUser();
  const admin = isAdmin(user);
  const reporting = isReporting(user);

  const [activeTab, setActiveTab] = useState<Tab>("configure");
  const [scope, setScope] = useState<Scope>("publisher");

  // Publisher Config selections
  const [selectedPublishers, setSelectedPublishers] = useState<number[]>([]);

  // DSP Config selections (publisher IDs are separate from Publisher Config)
  const [selectedDspPublishers, setSelectedDspPublishers] = useState<number[]>([]);
  const [selectedDsps, setSelectedDsps] = useState<number[]>([]);

  const [frequency, setFrequency] = useState<RejectionDurationMinutes>(10);
  const [dspFrequency, setDspFrequency] = useState<RejectionDurationMinutes>(10);
  const [info, setInfo] = useState<{
    publishers: PublisherBean[];
    dspList: DspBean[];
  }>({ publishers: [], dspList: [] });
  const [error, setError] = useState<string | undefined>();

  // Replaces local isCapturing boolean — driven by real API status
  const [configStatus, setConfigStatus] = useState<RejectionConfigStatus>("NOT_STARTED");
  const [configLoading, setConfigLoading] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const scopeRef = useRef<Scope>(scope);

  // Generate Report tab state
  const [filtersOpen, setFiltersOpen] = useState<string[]>(["filters"]);
  const [reportMode, setReportMode] = useState<ReportMode>("publisher");
  const [granularity, setGranularity] = useState<Granularity>("daily");
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [reportSelectedPubs, setReportSelectedPubs] = useState<number[]>([]);
  const [reportSelectedDsps, setReportSelectedDsps] = useState<number[]>([]);
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>({});
  const [sortConfig, setSortConfig] = useState<{
    key: string | null;
    order: "ascend" | "descend" | null;
  }>({ key: null, order: null });
  const [hoveredHeader, setHoveredHeader] = useState<string | null>(null);
  const [hoveredSortCell, setHoveredSortCell] = useState<string | null>(null);
  const [hoveredFilter, setHoveredFilter] = useState<string | null>(null);
  const [openFilterKey, setOpenFilterKey] = useState<string | null>(null);

  const handleScopeChange = (newScope: Scope) => {
    scopeRef.current = newScope;
    setScope(newScope);
  };
  const publisherMap = useMemo(
    () => new Map(info.publishers.filter((p) => p.id && p.name).map((p) => [p.id!, p.name!])),
    [info.publishers],
  );
  const dspMap = useMemo(
    () => new Map(info.dspList.filter((d) => d.id && d.name).map((d) => [d.id!, d.name!])),
    [info.dspList],
  );

  const publisherOptions = info.publishers
    .filter((p) => p.id != null && p.name != null)
    .map((p) => ({ id: p.id as number, name: p.name as string }));

  const dspOptions = info.dspList
    .filter((d) => d.id != null && d.name != null)
    .map((d) => ({ id: d.id as number, name: d.name as string }));

  // ── Data loading ───────────────────────────────────────────────────────────

  const loadData = async () => {
    try {
      if (admin || reporting) {
        const formInfo = await formDataLoader(
          FormDataType.Publishers,
          FormDataType.DspList
        );
        setInfo({
          publishers: formInfo.publishers ?? [],
          dspList: formInfo.dspList ?? [],
        });
      }
    } catch (e: any) {
      setError(e?.error ?? "Failed to load data");
    }
  };

  /**
   * GET /api/rejection-stats-config/pub  +  GET /api/rejection-stats-config/dsp
   * Called on mount to restore saved selections and current recording status.
   */
  const loadCurrentConfig = async () => {
    setConfigLoading(true);
    try {
      const [pubConfig, dspConfig] = await Promise.all([
        rejectionReasonApi.getConfig("pub"),
        rejectionReasonApi.getConfig("dsp"),
      ]);

      // Restore selections — pub and DSP configs have independent publisher ID sets
      setSelectedPublishers(pubConfig.publisherIds ?? []);
      setSelectedDspPublishers(dspConfig.publisherIds ?? []);
      setSelectedDsps(dspConfig.dspIds ?? []);
      if (pubConfig.durationMinutes) setFrequency(pubConfig.durationMinutes);
      if (dspConfig.durationMinutes) setDspFrequency(dspConfig.durationMinutes);

      // Restore scope based on which config is actually RECORDING
      // This fixes the refresh bug — if DSP was recording, scope must be "dsp"
      if (dspConfig.status === "RECORDING") {
        scopeRef.current = "dsp";
        setScope("dsp");
      } else if (pubConfig.status === "RECORDING") {
        scopeRef.current = "publisher";
        setScope("publisher");
      }

      // RECORDING takes priority over either config
      const activeStatus =
        pubConfig.status === "RECORDING" ? "RECORDING" :
        dspConfig.status === "RECORDING" ? "RECORDING" :
        pubConfig.status;
      setConfigStatus(activeStatus);

      if (activeStatus === "RECORDING") startPolling();
    } catch (e: any) {
      setError(e?.error ?? "Failed to load config");
    } finally {
      setConfigLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    loadCurrentConfig();
    return () => stopPolling();
  }, [admin, reporting]);

  // ── Polling ────────────────────────────────────────────────────────────────

  const startPolling = () => {
    stopPolling();
    pollRef.current = setInterval(pollStatus, POLL_INTERVAL_MS);
  };

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  /**
   * GET /api/rejection-stats-config/{type}
   * Polled every 30s while RECORDING to detect auto-stop.
   */
  const pollStatus = async () => {
    try {
      // Use scopeRef — not scope — so the interval closure always has the current value
      const type: RejectionConfigType = scopeRef.current === "publisher" ? "pub" : "dsp";
      const config = await rejectionReasonApi.getConfig(type);
      setConfigStatus(config.status);
      if (config.status !== "RECORDING") {
        stopPolling();
        toast.success("RR Capture has stopped.");
      }
    } catch {
      // silently ignore poll errors
    }
  };

  // ── Configure tab handlers ─────────────────────────────────────────────────

  /**
   * 1. PUT /api/rejection-stats-config/{type}  — save publisherIds, dspIds, durationMinutes
   * 2. POST /api/rejection-stats-config/{type}/start  — begin recording
   */
  const onStartCapture = async () => {
    const type: RejectionConfigType = scopeRef.current === "publisher" ? "pub" : "dsp";

    if (scope === "publisher" && selectedPublishers.length === 0) {
      toast.error("Please select at least one publisher.");
      return;
    }
    if (scope === "dsp" && selectedDspPublishers.length === 0) {
      toast.error("Please select at least one publisher.");
      return;
    }
    if (scope === "dsp" && selectedDsps.length === 0) {
      toast.error("Please select at least one DSP.");
      return;
    }

    setConfigLoading(true);
    try {
      // Step 1 — persist config (blocked by BE with 409 if already RECORDING)
      await rejectionReasonApi.updateConfig(type, {
        type: type === "pub" ? "PUB" : "DSP",
        publisherIds: scope === "publisher" ? selectedPublishers : selectedDspPublishers,
        ...(scope === "dsp" ? { dspIds: selectedDsps } : {}),
        durationMinutes: scope === "publisher" ? frequency : dspFrequency,
      });

      // Step 2 — start recording
      const result = await rejectionReasonApi.startCapture(type);
      setConfigStatus(result.status);
      scopeRef.current = scope; // lock scope to the one that started recording
      toast.success("RR Capture Is Started Running.");
      startPolling();
    } catch (e: any) {
      const msg = e?.error ?? "Failed to start capture";
      toast.error(msg);
      setError(msg);
    } finally {
      setConfigLoading(false);
    }
  };

  /**
   * POST /api/rejection-stats-config/{type}/stop
   */
  const onStopCapture = async () => {
    const type: RejectionConfigType = scopeRef.current === "publisher" ? "pub" : "dsp";
    setConfigLoading(true);
    try {
      const result = await rejectionReasonApi.stopCapture(type);
      setConfigStatus(result.status);
      stopPolling();
      toast.success("RR Capture Is Stopped.");
    } catch (e: any) {
      const msg = e?.error ?? "Failed to stop capture";
      toast.error(msg);
      setError(msg);
    } finally {
      setConfigLoading(false);
    }
  };

  const isCapturing = configStatus === "RECORDING";

  // ── Generate Report handlers ───────────────────────────────────────────────

  const onResetReport = () => {
    setDateRange(null);
    setGranularity("daily");
    setReportSelectedPubs([]);
    setReportSelectedDsps([]);
    setColumnFilters({});
    setSortConfig({ key: null, order: null });
    setReportData(null);
  };

  /**
   * Publisher mode → POST /api/rejection-stats-config/pub-rej-stats
   * DSP mode      → POST /api/rejection-stats-config/dsp-rej-stats
   * publisherIds required for BOTH modes per API spec.
   */
  const onGenerateReport = async () => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      toast.error("Please select a date range.");
      return;
    }
    if (reportMode === "publisher" && reportSelectedPubs.length === 0) {
      toast.error("Please select at least one publisher.");
      return;
    }
    if (reportMode === "dsp" && reportSelectedDsps.length === 0) {
      toast.error("Please select at least one DSP.");
      return;
    }

    setReportLoading(true);
    setColumnFilters({});
    setSortConfig({ key: null, order: null });
    setReportData(null);

    try {
      const body = {
        fromDate: dateRange[0].format("YYYY-MM-DD"),
        toDate: dateRange[1].format("YYYY-MM-DD"),
        ...(reportMode === "publisher"
          ? { publisherIds: reportSelectedPubs }
          : {
              dspIds: reportSelectedDsps,
              ...(reportSelectedPubs.length > 0 ? { publisherIds: reportSelectedPubs } : {}),
            }
        ),
        granularity,
      };

      const rows =
        reportMode === "publisher"
          ? await rejectionReasonApi.getPublisherRejectionStats(body)
          : await rejectionReasonApi.getDspRejectionStats(body);

      const report = buildReportFromApi(rows, reportMode, granularity, publisherMap, dspMap);
      setColumnFilters({});
      setSortConfig({
        key: report.headers[0] ? toKey(report.headers[0]) : null,
        order: "ascend",
      });
      setReportData(report);
      setFiltersOpen([]);
      toast.success("Report Generated Successfully");
    } catch (e: any) {
      const msg = e?.error ?? "Failed to generate report";
      toast.error(msg);
      setError(msg);
    } finally {
      setReportLoading(false);
    }
  };

  // ── Table data ─────────────────────────────────────────────────────────────

  const filteredRows = useMemo(() => {
    if (!reportData) return [];
    const activeFilters = Object.entries(columnFilters).filter(
      ([, vals]) => vals.length > 0
    );
    if (activeFilters.length === 0) return reportData.rows;
    return reportData.rows.filter((r) =>
      activeFilters.every(([k, vals]) => vals.includes(String(r[k])))
    );
  }, [reportData, columnFilters]);

  const sortedRows = useMemo(
    () => sortReportRows(filteredRows, sortConfig.key, sortConfig.order),
    [filteredRows, sortConfig],
  );

  const filterableKeys = useMemo(
    () => new Set(["publisher_name", "dsp_name", "rr_reason", "nbr_reason"]),
    []
  );

  const tableColumns = useMemo(() => {
    if (!reportData) return [];
    return reportData.headers.map((h) => {
      const key = toKey(h);
      const isReason = key === "rr_reason" || key === "nbr_reason";
      const isNumeric = key === "rejection_count" || key === "hour";

      const sortHint =
        sortConfig.key === key
          ? sortConfig.order === "ascend"
            ? "Click to sort descending"
            : "Click to sort ascending"
          : "Click to sort ascending";

      const description = HEADER_DESCRIPTIONS[h] ?? h;

      const col: any = {
        title: (
          <Tooltip
            title={description}
            placement="topLeft"
            open={hoveredHeader === key}
            overlayStyle={{ pointerEvents: "none" }}
          >
            <span
              onMouseEnter={() => setHoveredHeader(key)}
              onMouseLeave={() => setHoveredHeader(null)}
              onClick={(e) => e.stopPropagation()}
            >
              {h}
            </span>
          </Tooltip>
        ),
        dataIndex: key,
        key,
        width: isNumeric ? 160 : undefined,
        align: isNumeric ? ("right" as const) : ("left" as const),
        sorter: true,
        sortOrder: sortConfig.key === key ? sortConfig.order : null,
        showSorterTooltip: {
          title:
            hoveredHeader === key ||
            hoveredFilter === key ||
            openFilterKey === key
              ? null
              : sortHint,
          align: { offset: [0, -25] },
          open:
            hoveredSortCell === key &&
            hoveredHeader !== key &&
            hoveredFilter !== key &&
            openFilterKey !== key,
          mouseLeaveDelay: 0,
          overlayStyle: { pointerEvents: "none" },
        },
        onHeaderCell: () => ({
          onMouseEnter: () => setHoveredSortCell(key),
          onMouseLeave: () => setHoveredSortCell(null),
        }),
      };

      if (isReason) {
        col.render = (val: string) =>
          val ? (
            <Tag
              color={getReasonColor(val)}
              style={{ borderRadius: 6, padding: "2px 10px" }}
            >
              {val}
            </Tag>
          ) : null;
      }

      if (filterableKeys.has(key)) {
        const uniqueValues = Array.from(
          new Set(reportData.rows.map((r) => String(r[key])))
        ).filter((v) => v && v !== "undefined");

        const selectedValues = columnFilters[key] ?? [];

        col.filterDropdown = ({ confirm }: { confirm: () => void }) => (
          <ColumnFilterChecklist
            options={uniqueValues}
            selectedValues={selectedValues}
            searchPlaceholder={`Search ${h}...`}
            onApply={(values) =>
              setColumnFilters((prev) => ({ ...prev, [key]: values }))
            }
            onClose={confirm}
          />
        );

        col.filterDropdownOpen = openFilterKey === key;
        col.onFilterDropdownOpenChange = (visible: boolean) => {
          setOpenFilterKey(visible ? key : null);
          if (visible) {
            setHoveredSortCell(null);
            setHoveredFilter(null);
          }
        };

        col.filterIcon = renderColumnFilterIcon(
          h,
          selectedValues.length > 0,
          (open) => setHoveredFilter(open ? key : null)
        );
      }

      return col;
    });
  }, [
    reportData,
    columnFilters,
    filterableKeys,
    sortConfig,
    hoveredHeader,
    hoveredSortCell,
    hoveredFilter,
    openFilterKey,
  ]);

  const tableWidth = useMemo(() => {
    if (!reportData) return 0;
    return Math.max(900, reportData.headers.length * 180);
  }, [reportData]);

  const onExportCsv = () => {
    if (!reportData || sortedRows.length === 0) return;
    const csvRows: string[][] = [];
    csvRows.push(reportData.headers);
    sortedRows.forEach((row) => {
      csvRows.push(
        reportData.headers.map((h) => {
          const v = row[toKey(h)];
          return v != null ? String(v) : "";
        })
      );
    });
    const csvContent = Papa.unparse(csvRows);
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `rejection-reason-${dayjs().format("YYYYMMDD-HHmmss")}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  const configureContent = (
    <Spin spinning={configLoading}>
      <div style={{ marginBottom: 16 }}>
        <Radio.Group
          value={scope}
          onChange={(e) => handleScopeChange(e.target.value)}
          disabled={isCapturing}
        >
          <Radio value="publisher">Publisher Config</Radio>
          <Radio value="dsp">DSP Config</Radio>
        </Radio.Group>
      </div>

      <SelectionSection
        title="Publisher"
        options={publisherOptions}
        selectedIds={scope === "publisher" ? selectedPublishers : selectedDspPublishers}
        onChange={scope === "publisher" ? setSelectedPublishers : setSelectedDspPublishers}
        triggerLabel="+ Add Publisher"
        popoverProps={{ disabled: isCapturing }}
      />
      {scope === "dsp" && (
        <SelectionSection
          title="DSP"
          options={dspOptions}
          selectedIds={selectedDsps}
          onChange={setSelectedDsps}
          triggerLabel="+ Add DSP"
          popoverProps={{ disabled: isCapturing }}
        />
      )}

      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          gap: 32,
          marginTop: 32,
        }}
      >
        <div>
          <div style={{ marginBottom: 12, fontWeight: 500, color: "#333" }}>
            Select RR Frequency
          </div>
          <Radio.Group
            size="large"
            value={scope === "publisher" ? frequency : dspFrequency}
            onChange={(e) => {
              const val = e.target.value as RejectionDurationMinutes;
              scope === "publisher" ? setFrequency(val) : setDspFrequency(val);
            }}
            disabled={isCapturing}
          >
            <Radio.Button value={2} style={{ padding: "0 20px" }}>2 Min</Radio.Button>
            <Radio.Button value={10} style={{ padding: "0 20px" }}>10 Min</Radio.Button>
            <Radio.Button value={30} style={{ padding: "0 20px" }}>30 Min</Radio.Button>
            <Radio.Button value={60} style={{ padding: "0 20px" }}>60 Min</Radio.Button>
          </Radio.Group>
        </div>
        {isCapturing ? (
          <Button
            type="primary"
            size="large"
            onClick={onStopCapture}
            style={{ minWidth: 220, padding: "0 32px" }}
          >
            Stop RR Capture
          </Button>
        ) : (
          <Button
            type="primary"
            size="large"
            onClick={onStartCapture}
            style={{ minWidth: 220, padding: "0 32px" }}
          >
            Start RR Capture
          </Button>
        )}
      </div>
    </Spin>
  );

  const filtersBody = (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <Radio.Group
        value={reportMode}
        onChange={(e) => {
          setReportMode(e.target.value);
          setDateRange(null);
          setGranularity("daily");
          setReportSelectedPubs([]);
          setReportSelectedDsps([]);
          setReportData(null);
          setColumnFilters({});
          setSortConfig({ key: null, order: null });
        }}
      >
        <Radio value="publisher">Publisher</Radio>
        <Radio value="dsp">DSP</Radio>
      </Radio.Group>

      <div>
        <div style={{ marginBottom: 8, fontWeight: 500, color: "#333" }}>
          Date Range
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <RangePicker
            value={dateRange as any}
            onChange={(vals) => setDateRange(vals as [Dayjs, Dayjs] | null)}
            format="DD MMM YY"
            popupClassName="report-range-picker"
            style={{ width: "30%", height: 40 }}
          />
          <Radio.Group
            value={granularity}
            onChange={(e) => setGranularity(e.target.value)}
            buttonStyle="solid"
            size="large"
          >
            <Radio.Button value="hourly" style={{ padding: "0 18px" }}>
              Hourly
            </Radio.Button>
            <Radio.Button value="daily" style={{ padding: "0 18px" }}>
              Daily
            </Radio.Button>
          </Radio.Group>
        </div>
      </div>

      {/* Publisher always shown in Publisher mode only */}
      {reportMode === "publisher" && (
        <SelectionSection
          title="Publisher"
          options={publisherOptions}
          selectedIds={reportSelectedPubs}
          onChange={setReportSelectedPubs}
          triggerLabel="+ Add Publisher"
        />
      )}

      {/* DSP mode shows Publisher selector above DSP selector */}
      {reportMode === "dsp" && (
        <>
          <SelectionSection
            title="Publisher"
            options={publisherOptions}
            selectedIds={reportSelectedPubs}
            onChange={setReportSelectedPubs}
            triggerLabel="+ Add Publisher"
          />
          <SelectionSection
            title="DSP"
            options={dspOptions}
            selectedIds={reportSelectedDsps}
            onChange={setReportSelectedDsps}
            triggerLabel="+ Add DSP"
          />
        </>
      )}

      <div style={{ display: "flex", gap: 12 }}>
        <Button onClick={onResetReport} style={{ padding: "0 24px" }}>
          Reset
        </Button>
        <Button
          type="primary"
          onClick={onGenerateReport}
          loading={reportLoading}
          style={{ padding: "0 32px", minWidth: 200 }}
        >
          Generate Report
        </Button>
      </div>
    </div>
  );

  const reportSection = (
    <div style={{ marginTop: 24 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 20, fontWeight: 600, color: "#111" }}>
            {reportData ? "Report" : "Reports"}
          </span>
          <span
            style={{
              background: "#f0f0f0",
              color: "#555",
              padding: "2px 10px",
              borderRadius: 10,
              fontSize: 12,
            }}
          >
            {formatCount(reportData?.rows.length ?? 0)}
          </span>
        </div>
        <Button
          onClick={onExportCsv}
          disabled={!reportData || sortedRows.length === 0}
          icon={
            <DownOutlined
              rotate={-90}
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />
          }
        >
          Export CSV
        </Button>
      </div>

      {!reportData ? (
        <div
          style={{
            border: "1px solid #f0f0f0",
            borderRadius: 8,
            background: "#fff",
            padding: "64px 24px",
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontSize: 16,
              fontWeight: 500,
              color: "#333",
              marginBottom: 8,
            }}
          >
            No Reports Generated
          </div>
          <div style={{ color: "#888", fontSize: 13 }}>
            Select a publisher, set your date range and filters above, then
            click Generate Report to view rejection reason data.
          </div>
        </div>
      ) : (
        <Table
          columns={tableColumns}
          dataSource={sortedRows.map((r, i) => ({ key: i, ...r }))}
          scroll={{ x: tableWidth }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50"],
          }}
          onChange={(_, __, sorter) => {
            const s = Array.isArray(sorter) ? sorter[0] : sorter;
            setSortConfig((prev) => {
              const clickedKey = (s?.columnKey ?? s?.field ?? prev.key) as string | null;
              if (!clickedKey) return prev;
              return { key: clickedKey, order: s?.order ?? "ascend" };
            });
          }}
        />
      )}
    </div>
  );

  const generateContent = (
    <Spin spinning={reportLoading}>
      <Collapse
        activeKey={filtersOpen}
        onChange={(keys) =>
          setFiltersOpen(Array.isArray(keys) ? keys : [keys])
        }
      >
        <Collapse.Panel
          key="filters"
          header={
            <span style={{ fontWeight: 500, fontSize: 14 }}>Filters</span>
          }
        >
          {filtersBody}
        </Collapse.Panel>
      </Collapse>
      {reportSection}
    </Spin>
  );

  return (
    <div style={{ padding: 8 }}>
      {error && (
        <Alert
          type="error"
          message={error}
          style={{ marginBottom: 16 }}
          closable
          onClose={() => setError(undefined)}
        />
      )}

      <div style={{ marginBottom: 32 }}>
        <Segmented
          size="large"
          value={activeTab}
          onChange={(value) => setActiveTab(value as Tab)}
          options={[
            { label: <span style={{ padding: "0 16px" }}>RR Configure</span>, value: "configure" },
            { label: <span style={{ padding: "0 16px" }}>Generate Report</span>, value: "generate" },
          ]}
        />
      </div>

      {activeTab === "configure" ? configureContent : generateContent}
    </div>
  );
};

export default RejectionReason;